import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, eur, formatApiError } from "@/lib/api";
import { EVENT_GAMES, CASH_FACTOR } from "@/lib/constants";
import { useSelection, CheckAll, Check, BulkBar, runBulk, confirmDelete } from "@/components/admin/Bulk";

const inp = "h-10 w-full rounded-lg border border-black/15 bg-white px-3 text-sm outline-none focus:border-amber-500";
const EMPTY_EVENT = { title: "", game: "Magic", type: "Torneo", date: new Date().toISOString().slice(0, 10), time: "18:00", price: 0, capacity: 16, description: "" };

const EventForm = ({ initial, onSaved }) => {
  const [f, setF] = useState({ ...EMPTY_EVENT, ...initial });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, price: parseFloat(f.price) || 0, capacity: parseInt(f.capacity, 10) };
    try {
      if (initial?.id) await api.put(`/events/${initial.id}`, body);
      else await api.post("/events", body);
      toast.success("Evento salvato");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <form onSubmit={save} className="grid gap-3 sm:grid-cols-2" data-testid="event-form">
      <label className="space-y-1 sm:col-span-2"><span className="text-xs text-stone-500">Titolo</span><input required value={f.title} onChange={set("title")} className={inp} data-testid="ef-title" /></label>
      <label className="space-y-1"><span className="text-xs text-stone-500">Gioco</span><select value={f.game} onChange={set("game")} className={inp} data-testid="ef-game">{EVENT_GAMES.map((g) => <option key={g}>{g}</option>)}</select></label>
      <label className="space-y-1"><span className="text-xs text-stone-500">Tipo</span><select value={f.type} onChange={set("type")} className={inp} data-testid="ef-type">{["Casual", "Torneo", "Prerelease", "Evento speciale"].map((g) => <option key={g}>{g}</option>)}</select></label>
      <label className="space-y-1"><span className="text-xs text-stone-500">Data</span><input type="date" required value={f.date} onChange={set("date")} className={inp} data-testid="ef-date" /></label>
      <label className="space-y-1"><span className="text-xs text-stone-500">Ora</span><input type="time" required value={f.time} onChange={set("time")} className={inp} data-testid="ef-time" /></label>
      <label className="space-y-1"><span className="text-xs text-stone-500">Quota (€)</span><input type="number" step="0.5" min="0" value={f.price} onChange={set("price")} className={inp} data-testid="ef-price" /></label>
      <label className="space-y-1"><span className="text-xs text-stone-500">Posti</span><input type="number" min="1" value={f.capacity} onChange={set("capacity")} className={inp} data-testid="ef-capacity" /></label>
      <label className="space-y-1 sm:col-span-2"><span className="text-xs text-stone-500">Descrizione</span><textarea value={f.description} onChange={set("description")} rows={3} className={`${inp} h-auto py-2`} data-testid="ef-description" /></label>
      <button className="h-11 rounded-full bg-amber-500 font-bold text-black sm:col-span-2" data-testid="ef-save">Salva evento</button>
    </form>
  );
};

const BulkEventEdit = ({ ids, onDone }) => {
  const [c, setC] = useState({ price: "", capacity: "", game: "", type: "" });
  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    const changes = { price: c.price === "" ? null : parseFloat(c.price), capacity: c.capacity === "" ? null : parseInt(c.capacity, 10), game: c.game || null, type: c.type || null };
    if (await runBulk("/admin/bulk/events", { ids, action: "update", changes }, "Eventi aggiornati")) onDone();
  };
  return (
    <form onSubmit={save} className="grid gap-3 sm:grid-cols-2" data-testid="bulk-event-form">
      <p className="text-sm text-stone-600 sm:col-span-2">Compila solo i campi da cambiare nei {ids.length} eventi selezionati.</p>
      <label className="space-y-1"><span className="text-xs text-stone-500">Quota (€)</span><input type="number" step="0.5" min="0" value={c.price} onChange={set("price")} className={inp} data-testid="bulk-ef-price" /></label>
      <label className="space-y-1"><span className="text-xs text-stone-500">Posti</span><input type="number" min="1" value={c.capacity} onChange={set("capacity")} className={inp} data-testid="bulk-ef-capacity" /></label>
      <label className="space-y-1"><span className="text-xs text-stone-500">Gioco</span><select value={c.game} onChange={set("game")} className={inp} data-testid="bulk-ef-game"><option value="">—</option>{EVENT_GAMES.map((g) => <option key={g}>{g}</option>)}</select></label>
      <label className="space-y-1"><span className="text-xs text-stone-500">Tipo</span><select value={c.type} onChange={set("type")} className={inp} data-testid="bulk-ef-type"><option value="">—</option>{["Casual", "Torneo", "Prerelease", "Evento speciale"].map((g) => <option key={g}>{g}</option>)}</select></label>
      <button className="h-11 rounded-full bg-amber-500 font-bold text-black sm:col-span-2" data-testid="bulk-ef-save">Applica a {ids.length} eventi</button>
    </form>
  );
};

export const AdminEvents = () => {
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [bulkEdit, setBulkEdit] = useState(false);
  const [attendees, setAttendees] = useState(null);
  const s = useSelection(events);
  const load = () => api.get("/events", { params: { upcoming: false, limit: 200 } }).then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (e) => {
    if (!window.confirm(`Eliminare "${e.title}"?`)) return;
    await api.delete(`/events/${e.id}`);
    toast.success("Evento eliminato");
    load();
  };
  const bulkDelete = async () => {
    if (!confirmDelete(s.ids.length, "eventi")) return;
    if (await runBulk("/admin/bulk/events", { ids: s.ids, action: "delete" }, "Eventi eliminati")) { s.clear(); load(); }
  };
  const showAttendees = async (e) => setAttendees({ e, list: (await api.get(`/admin/events/${e.id}/attendees`)).data });
  return (
    <div>
      <button onClick={() => setEditing({})} className="flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 text-sm font-bold text-black" data-testid="admin-add-event"><Plus className="h-4 w-4" />Nuovo evento</button>
      <div className="mt-4">
        <BulkBar s={s} entity="events" prefix="events" onDelete={bulkDelete}>
          <button onClick={() => setBulkEdit(true)} className="flex items-center gap-1 rounded-full bg-black px-3 py-1 text-white" data-testid="events-bulk-edit"><Pencil className="h-3.5 w-3.5" />Modifica</button>
        </BulkBar>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-black/10 bg-white">
        <table className="w-full min-w-[720px] text-sm" data-testid="admin-events-table">
          <thead className="bg-[#F3E9D7] text-left text-xs uppercase tracking-wider text-stone-500"><tr><th className="w-10 p-3"><CheckAll s={s} testid="events-select-all" /></th><th className="p-3">Data</th><th className="p-3">Evento</th><th className="p-3">Gioco</th><th className="p-3">Quota</th><th className="p-3">Iscritti</th><th className="p-3" /></tr></thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id} className={`border-t border-black/[0.06] ${s.has(e.id) ? "bg-amber-50" : ""}`} data-testid={`admin-event-row-${e.id}`}>
                <td className="p-3"><Check s={s} id={e.id} testid={`event-select-${e.id}`} /></td>
                <td className="p-3 font-mono">{e.date} {e.time}</td>
                <td className="p-3 font-semibold">{e.title}<span className="block text-xs font-normal text-stone-500">{e.type}</span></td>
                <td className="p-3">{e.game}</td>
                <td className="p-3 font-mono">{e.price ? eur(e.price) : "Gratis"}</td>
                <td className="p-3"><button onClick={() => showAttendees(e)} className="flex items-center gap-1 hover:text-amber-600" data-testid={`admin-event-attendees-${e.id}`}><Users className="h-4 w-4" />{e.registered_count}/{e.capacity}</button></td>
                <td className="p-3 text-right">
                  <button onClick={() => setEditing(e)} className="p-2 hover:text-amber-600" data-testid={`admin-event-edit-${e.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(e)} className="p-2 hover:text-rose-500" data-testid={`admin-event-delete-${e.id}`}><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing?.id ? "Modifica evento" : "Nuovo evento"}</DialogTitle></DialogHeader>
          {editing && <EventForm initial={editing} onSaved={() => { setEditing(null); load(); }} />}
        </DialogContent>
      </Dialog>
      <Dialog open={bulkEdit} onOpenChange={setBulkEdit}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Modifica {s.ids.length} eventi</DialogTitle></DialogHeader>
          {bulkEdit && <BulkEventEdit ids={s.ids} onDone={() => { setBulkEdit(false); s.clear(); load(); }} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!attendees} onOpenChange={(o) => !o && setAttendees(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Iscritti – {attendees?.e.title}</DialogTitle></DialogHeader>
          <div className="space-y-1 text-sm" data-testid="attendees-list">
            {attendees?.list.length === 0 && <p className="text-stone-500">Nessun iscritto.</p>}
            {attendees?.list.map((u) => <p key={u.email}>{u.name} <span className="text-stone-500">– {u.email}</span></p>)}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const B2B_STATUS = { nuova: "bg-amber-500/15 text-amber-700", approvata: "bg-teal-500/15 text-teal-700", rifiutata: "bg-rose-500/15 text-rose-700", revocata: "bg-stone-500/15 text-stone-600" };

export const AdminB2B = () => {
  const [list, setList] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const s = useSelection(list);
  const load = () => {
    api.get("/admin/b2b-requests").then((r) => setList(r.data));
    api.get("/admin/b2b-accounts").then((r) => setAccounts(r.data));
  };
  useEffect(() => { load(); }, []);
  const act = async (path, body, msg) => {
    try { await api.post(path, body); toast.success(msg); load(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const bulk = async (action, label) => { if (await runBulk("/admin/bulk/b2b", { ids: s.ids, action }, label)) { s.clear(); load(); } };
  const rejected = list.filter((b) => ["rifiutata", "revocata"].includes(b.status));
  const delOne = async (b) => {
    if (!window.confirm(`Eliminare la richiesta ${b.status} di ${b.company}?`)) return;
    if (await runBulk("/admin/bulk/b2b", { ids: [b.id], action: "delete" }, "Richiesta eliminata")) load();
  };
  const purge = async () => {
    if (!window.confirm(`Eliminare tutte le ${rejected.length} richieste rifiutate o revocate? Le approvate e quelle in attesa restano.`)) return;
    try { const { data } = await api.delete("/admin/b2b-requests/rejected"); toast.success(`${data.deleted} richieste eliminate`); s.clear(); load(); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="space-y-6" data-testid="admin-b2b">
      <div className="rounded-2xl border border-black/10 bg-white p-5" data-testid="admin-b2b-accounts">
        <p className="font-display text-lg font-bold">Aziende con accesso B2B ({accounts.length})</p>
        <ul className="mt-2 divide-y divide-black/5 text-sm">
          {accounts.map((a) => (
            <li key={a.email} className="flex flex-wrap items-center gap-3 py-2" data-testid={`b2b-account-${a.email}`}>
              <span className="font-semibold">{a.company}</span><span className="text-stone-500">P.IVA {a.vat} · {a.email}</span>
              <span className={`text-xs ${a.registered ? "text-teal-700" : "text-amber-700"}`}>{a.registered ? "account attivo" : "non ancora registrato"}</span>
              <button onClick={() => window.confirm(`Revocare l'accesso B2B a ${a.company}?`) && act("/admin/b2b-accounts/revoke", { email: a.email }, "Accesso revocato")} className="ml-auto text-xs text-rose-600 underline" data-testid={`b2b-revoke-${a.email}`}>Revoca</button>
            </li>
          ))}
          {!accounts.length && <li className="py-2 text-stone-500">Nessuna azienda approvata.</li>}
        </ul>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <p className="font-display text-lg font-bold">Richieste di accesso ({list.length})</p>
        {list.length > 0 && <label className="flex items-center gap-1 text-xs"><CheckAll s={s} testid="b2b-select-all" />Seleziona tutte</label>}
        {rejected.length > 0 && <button onClick={purge} className="ml-auto flex items-center gap-1 rounded-full border border-rose-400 px-3 py-1 text-xs text-rose-600" data-testid="b2b-purge-rejected"><Trash2 className="h-3.5 w-3.5" />Elimina rifiutate e revocate ({rejected.length})</button>}
      </div>
      <BulkBar s={s} entity="b2b" prefix="b2b" onDelete={() => window.confirm("Verranno eliminate solo le richieste rifiutate o revocate tra quelle selezionate. Continuare?") && bulk("delete", "Richieste eliminate")}>
        <button onClick={() => bulk("approve", "Approvate (email inviata alle aziende)")} className="rounded-full bg-cyan-700 px-3 py-1 text-white" data-testid="b2b-bulk-approve">Approva</button>
        <button onClick={() => bulk("reject", "Rifiutate")} className="rounded-full border border-black/30 bg-white px-3 py-1" data-testid="b2b-bulk-reject">Rifiuta</button>
      </BulkBar>
      {list.length === 0 && <p className="py-8 text-center text-stone-500">Nessuna richiesta B2B</p>}
      {list.map((b) => (
        <div key={b.id} className={`rounded-2xl border border-black/10 p-5 text-sm ${s.has(b.id) ? "bg-amber-50" : "bg-white"}`} data-testid={`admin-b2b-${b.id}`}>
          <p className="flex flex-wrap items-center gap-2 font-display text-lg"><Check s={s} id={b.id} testid={`b2b-select-${b.id}`} />{b.company} <span className="text-sm text-stone-500">P.IVA {b.vat}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${B2B_STATUS[b.status] || B2B_STATUS.nuova}`} data-testid={`b2b-status-${b.id}`}>{b.status || "nuova"}</span>
            {["rifiutata", "revocata"].includes(b.status) && <button onClick={() => delOne(b)} className="ml-auto flex items-center gap-1 text-xs text-rose-600 underline" data-testid={`b2b-delete-${b.id}`}><Trash2 className="h-3.5 w-3.5" />Elimina</button>}</p>
          <p className="text-stone-600">{b.contact_name} · <a href={`mailto:${b.email}`} className="text-amber-600 underline">{b.email}</a> · {b.phone} · {b.city}, {b.country}</p>
          {(b.address || b.admin_tax_code || b.sdi || b.pec) && (
            <p className="mt-1 text-xs text-stone-600" data-testid={`b2b-billing-${b.id}`}>
              {b.address && <>Sede: {b.address}, {b.zip} {b.city}{b.province ? ` (${b.province})` : ""} · </>}
              {b.admin_tax_code && <>CF referente: <span className="font-mono">{b.admin_tax_code}</span> · </>}
              {b.sdi && <>SDI: <span className="font-mono">{b.sdi}</span> · </>}
              {b.pec && <>PEC: {b.pec}</>}
              {b.source === "registrazione" && <span className="ml-2 rounded bg-cyan-700/10 px-1.5 text-cyan-800">dalla registrazione</span>}
            </p>
          )}
          {b.message && <p className="mt-2 text-stone-600">{b.message}</p>}
          <p className="mt-2 text-xs text-stone-500">{new Date(b.created_at).toLocaleString("it-IT")}</p>
          {(b.status || "nuova") !== "approvata" && (
            <div className="mt-3 flex gap-2">
              <button onClick={() => act(`/admin/b2b-requests/${b.id}/approve`, {}, "Accesso B2B approvato: abbiamo avvisato l'azienda via email")} className="rounded-full bg-cyan-700 px-4 py-1.5 text-xs font-bold text-white" data-testid={`b2b-approve-${b.id}`}>Approva</button>
              {(b.status || "nuova") === "nuova" && <button onClick={() => act(`/admin/b2b-requests/${b.id}/reject`, {}, "Richiesta rifiutata")} className="rounded-full border border-black/20 px-4 py-1.5 text-xs" data-testid={`b2b-reject-${b.id}`}>Rifiuta</button>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export const AdminCredit = () => {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ email: "", offer: "", payout: "credito", note: "" });
  const load = () => api.get("/admin/valuations").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const offer = parseFloat(f.offer) || 0;
  const paid = f.payout === "credito" ? offer : offer * CASH_FACTOR;
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/valuations", { ...f, offer });
      toast.success(f.payout === "credito" ? `Accreditati ${eur(paid)} a ${f.email}` : `Registrato pagamento in contanti di ${eur(paid)}`);
      setF({ email: "", offer: "", payout: "credito", note: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <div className="grid gap-6 lg:grid-cols-[400px_1fr]">
      <form onSubmit={submit} className="h-fit space-y-3 rounded-2xl border-2 border-black bg-white p-5" data-testid="valuation-form">
        <p className="font-display text-lg">Nuova valutazione in negozio</p>
        <input required type="email" placeholder="Email account cliente" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inp} data-testid="valuation-email" />
        <input required type="number" step="0.01" min="0.01" placeholder="Proposta (€)" value={f.offer} onChange={(e) => setF({ ...f, offer: e.target.value })} className={inp} data-testid="valuation-offer" />
        <div className="grid grid-cols-2 gap-2">
          {[["credito", "Credito 100%"], ["contanti", `Contanti ${Math.round(CASH_FACTOR * 100)}%`]].map(([v, l]) => (
            <button type="button" key={v} onClick={() => setF({ ...f, payout: v })} data-testid={`valuation-payout-${v}`} className={`rounded-lg border-2 p-2 text-sm ${f.payout === v ? "border-black bg-amber-500" : "border-black/10"}`}>{l}</button>
          ))}
        </div>
        <input placeholder="Note (articoli valutati)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className={inp} data-testid="valuation-note" />
        <p className="text-sm">Da corrispondere: <b className="font-mono" data-testid="valuation-paid">{eur(paid)}</b> {f.payout === "credito" ? "(accreditati sull'account)" : "(in contanti)"}</p>
        <button className="h-10 w-full rounded-full bg-black text-white" data-testid="valuation-submit">Registra</button>
      </form>
      <div className="space-y-2" data-testid="valuation-list">
        {list.length === 0 && <p className="py-8 text-center text-stone-500">Nessuna valutazione registrata</p>}
        {list.map((v) => (
          <div key={v.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-black/10 bg-white p-4 text-sm">
            <span className="font-semibold">{v.email}</span>
            <span className="text-stone-500">{new Date(v.created_at).toLocaleString("it-IT")}</span>
            {v.note && <span className="text-stone-600">· {v.note}</span>}
            <span className="ml-auto">Proposta {eur(v.offer)} → <b>{eur(v.paid)}</b> {v.payout}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AdminNewsletter = () => {
  const [list, setList] = useState([]);
  const s = useSelection(list, "email");
  const load = () => api.get("/admin/newsletter").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const copy = () => { navigator.clipboard?.writeText((s.ids.length ? s.ids : list.map((x) => x.email)).join(", ")); toast.success("Email copiate"); };
  const bulkDelete = async () => {
    if (!confirmDelete(s.ids.length, "iscritti dalla newsletter")) return;
    if (await runBulk("/admin/bulk/newsletter", { ids: s.ids, action: "delete" }, "Iscritti rimossi")) { s.clear(); load(); }
  };
  return (
    <div data-testid="admin-newsletter">
      <div className="mb-3 flex items-center gap-3">
        {list.length > 0 && <CheckAll s={s} testid="newsletter-select-all" />}
        <p className="font-semibold">{list.length} iscritti</p>
        {list.length > 0 && <button onClick={copy} className="rounded-full border border-black/15 px-3 py-1 text-xs" data-testid="admin-newsletter-copy">{s.ids.length ? "Copia selezionate" : "Copia tutte"}</button>}
      </div>
      <BulkBar s={s} entity="newsletter" prefix="newsletter" onDelete={bulkDelete} />
      <div className="divide-y divide-black/5 rounded-2xl border border-black/10 bg-white">
        {list.map((x) => (
          <label key={x.email} className={`flex cursor-pointer items-center gap-3 px-4 py-2 text-sm ${s.has(x.email) ? "bg-amber-50" : ""}`} data-testid={`newsletter-row-${x.email}`}>
            <Check s={s} id={x.email} testid={`newsletter-select-${x.email}`} />
            <span>{x.email}</span><span className="ml-auto text-stone-500">{new Date(x.created_at).toLocaleDateString("it-IT")}</span>
          </label>
        ))}
      </div>
    </div>
  );
};
